import { DataType } from "./types";

// ─── Data type color/label config ─────────────────────────────────────────────

export const DATA_TYPE_CONFIG: Record<
  DataType,
  { label: string; bg: string; text: string; border: string }
> = {
  number: {
    label: "Number",
    bg: "bg-blue-50",
    text: "text-blue-700",
    border: "border-blue-200",
  },
  string: {
    label: "Text",
    bg: "bg-gray-100",
    text: "text-gray-700",
    border: "border-gray-200",
  },
  date: {
    label: "Date",
    bg: "bg-green-50",
    text: "text-green-700",
    border: "border-green-200",
  },
  boolean: {
    label: "Boolean",
    bg: "bg-orange-50",
    text: "text-orange-700",
    border: "border-orange-200",
  },
  currency: {
    label: "Currency",
    bg: "bg-purple-50",
    text: "text-purple-700",
    border: "border-purple-200",
  },
  percentage: {
    label: "%",
    bg: "bg-teal-50",
    text: "text-teal-700",
    border: "border-teal-200",
  },
  custom: {
    label: "ƒ",
    bg: "bg-amber-50",
    text: "text-amber-700",
    border: "border-amber-200",
  },
};

export const SOURCE_TYPE_LABELS: Record<string, string> = {
  sql: "Live Data",
  spreadsheet: "Spreadsheet / CSV",
  api: "API Source",
};

export const SOURCE_TYPE_COLORS: Record<string, string> = {
  sql: "text-indigo-600",
  spreadsheet: "text-emerald-600",
  api: "text-rose-600",
};

export const AGGREGATION_LABELS: Record<string, string> = {
  sum: "Sum",
  avg: "Average",
  count: "Count",
  min: "Min",
  max: "Max",
  count_distinct: "Count Distinct",
};

export const FORMULA_FUNCTIONS = [
  {
    name: "IF",
    signature: "IF(condition, true_value, false_value)",
    description:
      "Returns true_value if condition is met, otherwise false_value.",
    example: "IF({is_active}, {total_options}, 0)",
  },
  {
    name: "ROUND",
    signature: "ROUND(value, decimals)",
    description: "Rounds a number to the specified decimal places.",
    example: "ROUND({strike_price}, 2)",
  },
  {
    name: "CONCAT",
    signature: "CONCAT(text1, text2, ...)",
    description: "Joins multiple text values together.",
    example: 'CONCAT({employee_name}, " - ", {department})',
  },
  {
    name: "DATE_DIFF",
    signature: "DATE_DIFF(date1, date2, unit)",
    description:
      "Returns the difference between two dates in the given unit (days, months, years).",
    example: 'DATE_DIFF({vest_date}, {grant_date}, "months")',
  },
  {
    name: "COALESCE",
    signature: "COALESCE(value1, value2, ...)",
    description: "Returns the first non-null value.",
    example: "COALESCE({exercise_price}, {strike_price})",
  },
  {
    name: "SUM_IF",
    signature: "SUM_IF(condition, value)",
    description: "Sums values where condition is true.",
    example: "SUM_IF({is_active}, {total_options})",
  },
  {
    name: "ABS",
    signature: "ABS(value)",
    description: "Returns the absolute value.",
    example: "ABS({price_change_pct})",
  },
  {
    name: "MIN",
    signature: "MIN(value1, value2, ...)",
    description: "Returns the minimum of the provided values.",
    example: "MIN({total_options}, 1000)",
  },
  {
    name: "MAX",
    signature: "MAX(value1, value2, ...)",
    description: "Returns the maximum of the provided values.",
    example: "MAX({vested_options}, 0)",
  },
  {
    name: "NOW",
    signature: "NOW()",
    description: "Returns the current date and time.",
    example: "DATE_DIFF(NOW(), {grant_date}, 'days')",
  },
];

export const TEXT_OPERATOR_LABELS: Record<string, string> = {
  is: "is",
  is_not: "is not",
  contains: "contains",
  starts_with: "starts with",
  ends_with: "ends with",
  is_blank: "is blank",
};

export const NUMBER_OPERATOR_LABELS: Record<string, string> = {
  eq: "equals",
  neq: "not equals",
  gt: "greater than",
  lt: "less than",
  gte: "≥",
  lte: "≤",
  between: "between",
};

export const DATE_OPERATOR_LABELS: Record<string, string> = {
  is: "is",
  is_before: "is before",
  is_after: "is after",
  is_between: "is between",
  is_in_last: "is in the last",
};

export const BOOLEAN_OPERATOR_LABELS: Record<string, string> = {
  is_true: "is true",
  is_false: "is false",
};

export function getOperatorLabels(dataType: DataType): Record<string, string> {
  switch (dataType) {
    case "number":
    case "currency":
    case "percentage":
      return NUMBER_OPERATOR_LABELS;
    case "date":
      return DATE_OPERATOR_LABELS;
    case "boolean":
      return BOOLEAN_OPERATOR_LABELS;
    default:
      return TEXT_OPERATOR_LABELS;
  }
}

export function getDefaultOperator(dataType: DataType): string {
  switch (dataType) {
    case "number":
    case "currency":
    case "percentage":
      return "eq";
    case "date":
      return "is";
    case "boolean":
      return "is_true";
    default:
      return "is";
  }
}
